import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast.service';
import { SocialFeedService } from '../social-feed/services/social-feed.service';
import { CommunityPrivacy, SocialCommunity } from '../social-feed/models/social-feed.model';
import { communityError } from '../community/community-utils';
import { SocialHubService } from '../social-feed/services/social-hub.service';

const MAX_IMAGE_MB = 5;

/**
 * Create a community (/communities/new) or change one's settings
 * (/communities/:slug/settings). Who joins and who invites can only be
 * changed by the owner; moderators may edit the rest. Deleting is owner-only.
 */
@Component({
  selector: 'app-community-form',
  templateUrl: './community-form.component.html',
  styleUrls: ['./community-form.component.scss'],
  standalone: false,
})
export class CommunityFormComponent implements OnInit, OnDestroy {
  slug: string | null = null;
  community: SocialCommunity | null = null;
  loading = false;
  notAllowed = false;

  name = '';
  description = '';
  rules = '';
  privacy: CommunityPrivacy = 'public';
  membersCanInvite = true;

  imageFile: File | null = null;
  imagePreview: string | null = null;
  removeImage = false;
  imageError = '';

  saving = false;
  formError = '';
  submitted = false;

  confirmDelete = false;
  deleteName = '';
  deleting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private social: SocialFeedService,
    private toaster: ToastService,
    private hub: SocialHubService,
    private cdr: ChangeDetectorRef
  ) {}

  get isEdit(): boolean {
    return !!this.slug;
  }

  get isOwner(): boolean {
    return !this.isEdit || this.community?.my_role === 'owner';
  }

  get nameError(): string {
    const n = this.name.trim();
    if (!n) return 'Give your community a name.';
    if (n.length < 3) return 'Use at least 3 characters.';
    return '';
  }

  ngOnInit(): void {
    this.slug = this.route.snapshot.paramMap.get('slug');
    if (this.slug) {
      this.loading = true;
      this.social.getCommunity(this.slug).subscribe({
        next: (c) => {
          this.community = c;
          this.loading = false;
          if (!c.can_manage) {
            this.notAllowed = true;
          } else {
            this.name = c.name;
            this.description = c.description;
            this.rules = c.rules;
            this.privacy = c.privacy;
            this.membersCanInvite = c.members_can_invite;
            this.imagePreview = c.image ? this.social.resolveImageUrl(c.image) : null;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.notAllowed = true;
          this.cdr.detectChanges();
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.revokePreview();
  }

  private revokePreview(): void {
    if (this.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(this.imagePreview);
  }

  onImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    this.imageError = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) {
      this.imageError = 'Choose a PNG, JPG, WebP or GIF picture.';
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      this.imageError = `The picture must be smaller than ${MAX_IMAGE_MB}MB.`;
      return;
    }
    this.revokePreview();
    this.imageFile = file;
    this.removeImage = false;
    this.imagePreview = URL.createObjectURL(file);
  }

  clearImage(): void {
    this.revokePreview();
    this.imageFile = null;
    this.imagePreview = null;
    this.removeImage = this.isEdit;
  }

  save(): void {
    this.submitted = true;
    this.formError = '';
    if (this.nameError || this.saving) return;
    this.saving = true;
    const fields: Record<string, string | boolean> = {
      name: this.name.trim(),
      description: this.description.trim(),
      rules: this.rules.trim(),
    };
    if (this.isOwner) {
      fields['privacy'] = this.privacy;
      fields['members_can_invite'] = this.membersCanInvite;
    }
    this.social.saveCommunity(fields, this.imageFile, this.slug || undefined, this.removeImage).subscribe({
      next: (c) => {
        this.saving = false;
        this.hub.refreshCommunities();
        this.toaster.showSuccess(this.isEdit ? 'Changes saved.' : `${c.name} is ready. Invite some people!`);
        this.router.navigate(['/communities', c.slug], { queryParams: this.isEdit ? {} : { tab: 'invite' } });
      },
      error: (err) => {
        this.saving = false;
        this.formError = communityError(err, "Couldn't save. Please try again.");
        this.cdr.detectChanges();
      },
    });
  }

  cancel(): void {
    this.router.navigate(this.slug ? ['/communities', this.slug] : ['/communities']);
  }

  doDelete(): void {
    if (!this.slug || this.deleting || this.deleteName.trim() !== this.community?.name) return;
    this.deleting = true;
    this.social.deleteCommunity(this.slug).subscribe({
      next: () => {
        this.hub.refreshCommunities();
        this.toaster.showSuccess('Community deleted.');
        this.router.navigate(['/communities'], { queryParams: { tab: 'mine' } });
      },
      error: (err) => {
        this.deleting = false;
        this.toaster.showNote(communityError(err, "Couldn't delete the community."), 'danger');
        this.cdr.detectChanges();
      },
    });
  }
}
